import { create } from 'zustand';
import type {
  AnalyticsData,
  AnalyticsPeriod,
  AnalyticsTab,
  MarginsData,
  WinLossData,
  DiscountDepthData,
  ModelEffectivenessEntry,
  TrendEntry,
  PriceWaterfallData,
} from '../types/analytics.types';
import { analyticsService } from '../services/analytics-service';

interface AnalyticsState {
  // Overview
  data: AnalyticsData | null;
  period: AnalyticsPeriod;
  isLoading: boolean;
  error: string | null;

  // Active tab
  activeTab: AnalyticsTab;

  // Tab data (loaded on demand)
  marginsData: MarginsData | null;
  winLossData: WinLossData | null;
  discountData: DiscountDepthData | null;
  modelsData: ModelEffectivenessEntry[] | null;
  trendsData: TrendEntry[] | null;
  waterfallData: PriceWaterfallData | null;

  // Tab loading states
  isLoadingTab: boolean;

  // Actions
  setPeriod: (period: AnalyticsPeriod) => void;
  setActiveTab: (tab: AnalyticsTab) => void;
  fetchOverview: (period?: AnalyticsPeriod) => Promise<void>;
  fetchMargins: () => Promise<void>;
  fetchWinLoss: () => Promise<void>;
  fetchDiscountDepth: () => Promise<void>;
  fetchModelEffectiveness: () => Promise<void>;
  fetchTrends: () => Promise<void>;
  fetchPriceWaterfall: () => Promise<void>;
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  data: null,
  period: '30d',
  isLoading: false,
  error: null,
  activeTab: 'overview',
  marginsData: null,
  winLossData: null,
  discountData: null,
  modelsData: null,
  trendsData: null,
  waterfallData: null,
  isLoadingTab: false,

  setPeriod: (period) => {
    set({
      period,
      // Reset all cached tab data so they refetch with new period
      marginsData: null,
      winLossData: null,
      discountData: null,
      modelsData: null,
      trendsData: null,
      waterfallData: null,
    });
    get().fetchOverview(period);
    // Refetch active tab data if not overview
    const activeTab = get().activeTab;
    if (activeTab !== 'overview') {
      get().setActiveTab(activeTab);
    }
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab });
    const state = get();
    switch (tab) {
      case 'margins':
        if (!state.marginsData) state.fetchMargins();
        break;
      case 'win-loss':
        if (!state.winLossData) state.fetchWinLoss();
        break;
      case 'discounts':
        if (!state.discountData) state.fetchDiscountDepth();
        break;
      case 'models':
        if (!state.modelsData) state.fetchModelEffectiveness();
        break;
      case 'trends':
        if (!state.trendsData) state.fetchTrends();
        break;
      case 'waterfall':
        if (!state.waterfallData) state.fetchPriceWaterfall();
        break;
    }
  },

  fetchOverview: async (period) => {
    const p = period || get().period;
    set({ isLoading: true, error: null });
    try {
      const data = await analyticsService.getOverview(p);
      set({ data, isLoading: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar analitica', isLoading: false });
    }
  },

  fetchMargins: async () => {
    set({ isLoadingTab: true });
    try {
      const marginsData = await analyticsService.getMargins(get().period);
      set({ marginsData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar márgenes', isLoadingTab: false });
    }
  },

  fetchWinLoss: async () => {
    set({ isLoadingTab: true });
    try {
      const winLossData = await analyticsService.getWinLoss(get().period);
      set({ winLossData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar ganancia/perdida', isLoadingTab: false });
    }
  },

  fetchDiscountDepth: async () => {
    set({ isLoadingTab: true });
    try {
      const discountData = await analyticsService.getDiscountDepth(get().period);
      set({ discountData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar descuentos', isLoadingTab: false });
    }
  },

  fetchModelEffectiveness: async () => {
    set({ isLoadingTab: true });
    try {
      const modelsData = await analyticsService.getModelEffectiveness();
      set({ modelsData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar modelos', isLoadingTab: false });
    }
  },

  fetchTrends: async () => {
    set({ isLoadingTab: true });
    try {
      const trendsData = await analyticsService.getTrends();
      set({ trendsData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar tendencias', isLoadingTab: false });
    }
  },

  fetchPriceWaterfall: async () => {
    set({ isLoadingTab: true });
    try {
      const waterfallData = await analyticsService.getPriceWaterfall(get().period);
      set({ waterfallData, isLoadingTab: false });
    } catch (err: any) {
      set({ error: err.message || 'Error al cargar waterfall', isLoadingTab: false });
    }
  },
}));
